import type { BodyRegionName, SorenessArea } from '@cadencia/contracts';
import type { ReactNode } from 'react';
import './escala.css';

const ZONAS: { region: BodyRegionName; etiqueta: string }[] = [
  { region: 'chest', etiqueta: 'Pecho' },
  { region: 'upper_back', etiqueta: 'Espalda alta' },
  { region: 'lower_back', etiqueta: 'Zona lumbar' },
  { region: 'shoulders_front', etiqueta: 'Hombro anterior' },
  { region: 'shoulders_side', etiqueta: 'Hombro lateral' },
  { region: 'shoulders_rear', etiqueta: 'Hombro posterior' },
  { region: 'biceps', etiqueta: 'Bíceps' },
  { region: 'triceps', etiqueta: 'Tríceps' },
  { region: 'forearms', etiqueta: 'Antebrazos' },
  { region: 'abs', etiqueta: 'Abdomen' },
  { region: 'quads', etiqueta: 'Cuádriceps' },
  { region: 'hamstrings', etiqueta: 'Isquios' },
  { region: 'glutes', etiqueta: 'Glúteos' },
  { region: 'calves', etiqueta: 'Gemelos' },
  { region: 'hip', etiqueta: 'Cadera' },
  { region: 'knee', etiqueta: 'Rodilla' },
];

/**
 * Articulaciones del mapa de DOLOR (23/09/2026). Hasta entonces el mapa solo
 * marcaba agujetas (`doms`) y nada en la aplicación producía `discomfort`: la
 * regla de molestia articular persistente leía un dato que nadie escribía.
 * El hombro va como `shoulders_front`, que es la región a la que la política de
 * seguridad asocia los patrones que cargan la articulación.
 */
const ARTICULACIONES: { region: BodyRegionName; etiqueta: string }[] = [
  { region: 'shoulders_front', etiqueta: 'Hombro' },
  { region: 'elbow', etiqueta: 'Codo' },
  { region: 'wrist', etiqueta: 'Muñeca' },
  { region: 'neck', etiqueta: 'Cuello' },
  { region: 'lower_back', etiqueta: 'Zona lumbar' },
  { region: 'hip', etiqueta: 'Cadera' },
  { region: 'knee', etiqueta: 'Rodilla' },
  { region: 'ankle', etiqueta: 'Tobillo' },
];

/**
 * Selección de zonas con agujetas o molestias.
 * Cada pulsación sube la intensidad 1→5 y vuelve a apagar la zona.
 * NO diagnostica: solo registra zona, intensidad y tipo (requisito §15.1).
 */
export function MapaCorporal({
  seleccion,
  onChange,
  tipo = 'doms',
}: {
  seleccion: SorenessArea[];
  onChange: (areas: SorenessArea[]) => void;
  /** `doms`: agujetas, por músculos. `discomfort`: dolor, por articulaciones. */
  tipo?: 'doms' | 'discomfort';
}): ReactNode {
  const zonas = tipo === 'discomfort' ? ARTICULACIONES : ZONAS;
  const nada = tipo === 'discomfort' ? 'sin dolor' : 'sin agujetas';
  const intensidadDe = (region: BodyRegionName): number =>
    seleccion.find((area) => area.region === region)?.intensity ?? 0;

  const alternar = (region: BodyRegionName): void => {
    const actual = intensidadDe(region);
    const siguiente = actual >= 5 ? 0 : actual + 1;
    const resto = seleccion.filter((area) => area.region !== region);
    onChange(
      siguiente === 0
        ? resto
        : [...resto, { region, intensity: siguiente, kind: tipo }],
    );
  };

  return (
    <div>
      <p className="campo__pista">
        {tipo === 'discomfort'
          ? 'Pulsa la articulación que te duele. Cada pulsación sube la intensidad (1 a 5); a la sexta se apaga.'
          : 'Pulsa una zona para marcar agujetas. Cada pulsación sube la intensidad (1 a 5); a la sexta se apaga.'}
      </p>
      <div className="mapa">
        {zonas.map((zona) => {
          const intensidad = intensidadDe(zona.region);
          return (
            <button
              key={zona.region}
              type="button"
              aria-pressed={intensidad > 0}
              aria-label={
                intensidad > 0
                  ? `${zona.etiqueta}: intensidad ${intensidad} de 5`
                  : `${zona.etiqueta}: ${nada}`
              }
              className={`mapa__zona${intensidad > 0 ? ' mapa__zona--activa' : ''}`}
              onClick={() => alternar(zona.region)}
            >
              <span>{zona.etiqueta}</span>
              {intensidad > 0 && <span className="mapa__intensidad">{intensidad}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
